// Pure helpers for the super admin console: labels, filters, URL <-> state, API parameters and
// display rules. Kept free of React and network code so they're unit-tested in
// consoleModel.test.js. Online status itself comes from the server (vTrackApp SuperAdminRules);
// speed and ignition display go through the shared rules in utils/deviceStatus.js.

import {DEFAULT_DEVICE_TAB_ORDER} from "../../utils/dashboardTabOrder";
import {gt06SpeedIsStale, isFt920} from "../../utils/deviceStatus";
import {PAGE_SIZE_OPTIONS} from "../../utils/paginationUtils";

/** Device types in the dashboard's default tab order. */
export const DEVICE_TYPES = DEFAULT_DEVICE_TAB_ORDER;

export const TYPE_LABELS = {
  GT06_TRACKER: "GPS Tracker",
  GPS_LOCK: "GPS Lock",
  BT_LOCK: "BLE Lock",
  MOBI_TRACK: "Track Point",
};

export const TYPE_PLURALS = {
  GT06_TRACKER: "GPS Trackers",
  GPS_LOCK: "GPS Locks",
  BT_LOCK: "BLE Locks",
  MOBI_TRACK: "Track Point",
};

export const ROLE_LABELS = {
  SUPER_ADMIN: "Super admin",
  CLIENT_ADMIN: "Admin",
  CLIENT_MANAGER: "Manager",
  CLIENT_EMPLOYEE: "Employee",
  MOBI_TRACK: "Track Point driver",
};

export const STATUS_LABELS = {
  ACTIVE: "Active",
  ASSIGNED: "Assigned",
  SUSPENDED: "Suspended",
  TRACKED: "Tracked",
};

export const ATTENTION_LABELS = {
  NOTHING_ONLINE: "Nothing online",
  MANY_OFFLINE: "Many offline",
};

export const PAGE_SIZES = PAGE_SIZE_OPTIONS;

export const VIEWS = ["clients", "devices", "client"];

// One filter set per list. Defaults are left out of the URL so links stay short.
export const DEFAULT_FILTERS = {
  clients: {q: "", status: "", type: "", attention: "", sort: "name", page: 0, size: 10},
  devices: {q: "", client: "", type: "", online: "any", status: "", sort: "name", page: 0, size: 10},
  clientDevices: {q: "", type: "", online: "any", status: "", sort: "name", page: 0, size: 10},
  users: {q: "", role: "", status: "", page: 0, size: 10},
};

// Allowed values per filter; anything else in a URL falls back to the default.
const ALLOWED = {
  status: {
    clients: ["", "ACTIVE", "INACTIVE"],
    devices: ["", "ACTIVE", "ASSIGNED", "SUSPENDED"],
    clientDevices: ["", "ACTIVE", "ASSIGNED", "SUSPENDED"],
    users: ["", "ACTIVE", "SUSPENDED"],
  },
  type: ["", ...DEVICE_TYPES],
  attention: ["", "yes", "no"],
  online: ["any", "online", "offline", "nolive"],
  role: ["", "CLIENT_ADMIN", "CLIENT_MANAGER", "CLIENT_EMPLOYEE", "MOBI_TRACK"],
  sort: {
    clients: ["name", "devices", "offline", "heard"],
    devices: ["name", "recent", "oldest", "client"],
    clientDevices: ["name", "recent", "oldest"],
  },
};

export const CLIENT_SORTS = [
  {value: "name", label: "Name A–Z"},
  {value: "devices", label: "Most devices"},
  {value: "offline", label: "Most offline"},
  {value: "heard", label: "Recently heard"},
];

export const DEVICE_SORTS = [
  {value: "name", label: "Name A–Z"},
  {value: "recent", label: "Last contact, newest"},
  {value: "oldest", label: "Last contact, oldest"},
];

export const ONLINE_OPTIONS = [
  {value: "any", label: "Any"},
  {value: "online", label: "Online"},
  {value: "offline", label: "Offline"},
  {value: "nolive", label: "No live status (BLE)"},
];

/** Which filter set the current view's main list uses. */
export const scopeOf = (view, tab) =>
  view === "client" ? (tab === "users" ? "users" : "clientDevices") : view;

const toInt = (value, fallback) => {
  const n = Number.parseInt(value, 10);
  return Number.isFinite(n) && n >= 0 ? n : fallback;
};

const pick = (value, allowed, fallback) => (allowed.includes(value) ? value : fallback);

function sanitize(scope, raw) {
  const defaults = DEFAULT_FILTERS[scope];
  const f = {...defaults};
  Object.keys(defaults).forEach((key) => {
    const value = raw[key];
    if (value === undefined || value === null) return;
    if (key === "page") {
      // Pages are 1-based in the URL for people, 0-based for the API.
      f.page = Math.max(0, toInt(value, 1) - 1);
    } else if (key === "size") {
      const size = toInt(value, defaults.size);
      f.size = PAGE_SIZES.includes(size) ? size : defaults.size;
    } else if (key === "q") {
      f.q = String(value).slice(0, 100);
    } else if (key === "client") {
      f.client = /^\d+$/.test(String(value)) ? String(value) : "";
    } else if (key === "status") {
      f.status = pick(value, ALLOWED.status[scope], "");
    } else if (key === "sort") {
      f.sort = pick(value, ALLOWED.sort[scope], defaults.sort);
    } else if (ALLOWED[key]) {
      f[key] = pick(value, ALLOWED[key], defaults[key]);
    }
  });
  return f;
}

/** Reads the console state from the address bar's query string. */
export function stateFromQuery(search) {
  const params = new URLSearchParams(search || "");
  const raw = Object.fromEntries(params.entries());
  let view = pick(raw.view, VIEWS, "clients");
  const clientId = /^\d+$/.test(raw.client || "") ? Number(raw.client) : null;
  if (view === "client" && clientId == null) view = "clients";
  const tab = raw.tab === "users" ? "users" : "devices";
  const deviceId = /^\d+$/.test(raw.device || "") ? Number(raw.device) : null;
  const filters = {
    clients: {...DEFAULT_FILTERS.clients},
    devices: {...DEFAULT_FILTERS.devices},
    clientDevices: {...DEFAULT_FILTERS.clientDevices},
    users: {...DEFAULT_FILTERS.users},
  };
  const scope = scopeOf(view, tab);
  // In the client view "client" is the selected client, not a filter.
  const scopeRaw = view === "client" ? {...raw, client: undefined} : raw;
  filters[scope] = sanitize(scope, scopeRaw);
  return {view, clientId: view === "client" ? clientId : null, tab, deviceId, filters};
}

/** Writes the console state back to a query string, leaving defaults out. */
export function queryFromState(state) {
  const params = new URLSearchParams();
  if (state.view !== "clients") params.set("view", state.view);
  if (state.view === "client" && state.clientId != null) params.set("client", String(state.clientId));
  if (state.view === "client" && state.tab === "users") params.set("tab", "users");
  const scope = scopeOf(state.view, state.tab);
  const f = state.filters[scope];
  const defaults = DEFAULT_FILTERS[scope];
  Object.keys(defaults).forEach((key) => {
    const value = f[key];
    if (value === defaults[key] || value === "" || value == null) return;
    if (key === "page") params.set("page", String(value + 1));
    else params.set(key, String(value));
  });
  if (state.deviceId != null) params.set("device", String(state.deviceId));
  return params.toString();
}

/** True when any filter (not paging) differs from the defaults. */
export const hasActiveFilters = (scope, f) =>
  Object.keys(DEFAULT_FILTERS[scope]).some(
    (key) => key !== "page" && key !== "size" && f[key] !== DEFAULT_FILTERS[scope][key]
  );

const compact = (obj) =>
  Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== "" && v != null));

/** Query parameters for GET /super-admin/clients. */
export const clientsParams = (f) =>
  compact({q: f.q.trim(), status: f.status, type: f.type, attention: f.attention, sort: f.sort, page: f.page, size: f.size});

/** Query parameters for GET /super-admin/devices (all devices, or one client's). */
export const devicesParams = (f, clientId = null) =>
  compact({
    clientId: clientId != null ? clientId : f.client,
    q: f.q.trim(),
    type: f.type,
    online: f.online === "any" ? "" : f.online,
    status: f.status,
    sort: f.sort,
    page: f.page,
    size: f.size,
  });

/** Query parameters for GET /super-admin/clients/{id}/users. */
export const usersParams = (f) =>
  compact({q: f.q.trim(), role: f.role, status: f.status, page: f.page, size: f.size});

/** "just now", "5 min ago", "3 h ago", "2 d ago", or "Never". */
export function timeAgo(iso, now = Date.now()) {
  if (!iso) return "Never";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "Never";
  const minutes = Math.floor(Math.max(0, now - t) / 60000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} min ago`;
  if (minutes < 1440) return `${Math.floor(minutes / 60)} h ago`;
  return `${Math.floor(minutes / 1440)} d ago`;
}

/** The online pill for one device: tone is "on", "off" or "none". */
export function onlineBadge(device) {
  if (!device) return {tone: "none", label: "—"};
  if (device.assetType === "BT_LOCK" || device.online == null) {
    return {tone: "none", label: "No live status"};
  }
  if (device.assetType === "MOBI_TRACK" && device.onDuty === false) {
    return {tone: "none", label: "Off duty"};
  }
  return device.online ? {tone: "on", label: "Online"} : {tone: "off", label: "Offline"};
}

const titleCase = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : s);

/**
 * Speed as the dashboards show it: only while online and only with a GPS fix from the last
 * 6 minutes (the server's positionStale, else deviceStatus.js). kmh is the number shown, or null.
 */
export function trackerSpeed(device) {
  const t = device.telemetry || {};
  const stale = device.positionStale ?? gt06SpeedIsStale(device.assetType, t);
  if (!device.online) return {value: "—", kmh: null, note: "Device offline"};
  if (stale) return {value: "—", kmh: null, note: "No GPS fix in the last 6 min"};
  const speed = Number(t.speed);
  if (!Number.isFinite(speed)) return {value: "—", kmh: null, note: null};
  return {value: `${Math.round(speed)} km/h`, kmh: Math.round(speed), note: null};
}

/** Ignition as the dashboards show it: N/A for FT920, otherwise accOn while online, else off. */
export function trackerIgnition(device) {
  if (isFt920(device)) return {value: "N/A", note: "FT920 units have no ignition reading"};
  if (!device.online) return {value: "Off", note: "Shown as off while offline"};
  return {value: device.telemetry?.accOn === true ? "On" : "Off", note: null};
}

const batteryPercent = (t) => {
  const n = parseInt(String(t?.batteryPercentage ?? "").replace(/%$/, ""), 10);
  return Number.isFinite(n) ? n : null;
};

/**
 * The live readings at the top of the device panel, per device type. tone colours the value:
 * "on" (good / running), "warn" (needs a look), "off" (bad) or "none".
 */
export function deviceVitals(device, now = Date.now()) {
  const t = device.telemetry || {};
  if (device.assetType === "GT06_TRACKER") {
    const speed = trackerSpeed(device);
    const ignition = trackerIgnition(device);
    return [
      {key: "speed", label: "Speed", value: speed.kmh == null ? "—" : String(speed.kmh), unit: speed.kmh == null ? null : "km/h", note: speed.note, tone: "none"},
      {key: "ignition", label: "Ignition", value: ignition.value, note: ignition.note, tone: ignition.value === "On" ? "on" : "none"},
      {key: "fix", label: "Last fix", value: timeAgo(device.lastFixUtc, now), note: null, tone: "none"},
    ];
  }
  if (device.assetType === "GPS_LOCK") {
    const battery = batteryPercent(t);
    return [
      {
        key: "lock",
        label: "Lock",
        value: t.lockStatus == null ? "—" : t.lockStatus ? "Locked" : "Open",
        note: device.online ? null : "Last known state",
        tone: t.lockStatus == null ? "none" : t.lockStatus ? "on" : "warn",
      },
      {key: "battery", label: "Battery", value: battery == null ? "—" : `${battery}%`, note: null, tone: battery != null && battery < 20 ? "off" : "none"},
      {key: "contact", label: "Last seen", value: timeAgo(device.lastContactUtc, now), note: null, tone: "none"},
    ];
  }
  if (device.assetType === "BT_LOCK") {
    const battery = batteryPercent(t);
    return [
      {key: "battery", label: "Battery", value: battery == null ? "—" : `${battery}%`, note: "From the last report", tone: battery != null && battery < 20 ? "off" : "none"},
      {
        key: "report",
        label: "Last report",
        value: timeAgo(device.lastFixUtc || device.lastContactUtc, now),
        note: "Sent through the Yekkada app over Bluetooth",
        tone: "none",
      },
    ];
  }
  if (device.assetType === "MOBI_TRACK") {
    return [
      {key: "duty", label: "Duty", value: device.onDuty ? "On duty" : "Off duty", note: null, tone: device.onDuty ? "on" : "none"},
      {key: "position", label: "Last position", value: timeAgo(device.lastFixUtc, now), note: null, tone: "none"},
    ];
  }
  return [];
}

const UNIT_ID_LABELS = {GT06_TRACKER: "IMEI", GPS_LOCK: "Device ID", BT_LOCK: "Lock ID", MOBI_TRACK: "Phone"};

/**
 * The device panel's details list. person is who the device is assigned to: the active
 * assignment, or for Track Point the driver the phone belongs to (decided on the server).
 */
export function deviceDetails(device) {
  const trackPoint = device.assetType === "MOBI_TRACK";
  const rows = [
    {
      key: "person",
      label: trackPoint ? "Driver" : "Assigned to",
      value: device.assignedToName || "Not assigned",
      muted: !device.assignedToName,
      note: device.assignedToName ? null : trackPoint ? "No driver account uses this phone" : "Client admins assign devices from Assets",
    },
    {key: "unitId", label: UNIT_ID_LABELS[device.assetType] || "Unit ID", value: device.unitId || "—", mono: true, copy: !!device.unitId},
  ];
  if (device.assetType === "GT06_TRACKER") {
    if (device.vehicleType) rows.push({key: "vehicle", label: "Vehicle", value: titleCase(device.vehicleType)});
    rows.push({key: "protocol", label: "Protocol", value: isFt920(device) ? "FT920" : "GT06"});
  }
  rows.push({key: "status", label: "Status", value: STATUS_LABELS[device.assetStatus] || device.assetStatus || "—"});
  rows.push({
    key: "registered",
    label: "Registered",
    value: device.registeredAt ? new Date(device.registeredAt).toLocaleDateString(undefined, {day: "numeric", month: "short", year: "numeric"}) : "—",
  });
  return rows;
}

const hasPosition = (d) => Number.isFinite(d?.latitude) && Number.isFinite(d?.longitude);

/** The live map works for every device type except BLE locks, once a position is known. */
export const canOpenLiveMap = (device) => !!device && device.assetType !== "BT_LOCK" && hasPosition(device);

/** Route history works for Track Point (the tracker route-history endpoint isn't on the backend). */
export const canOpenRouteHistory = (device) => device?.assetType === "MOBI_TRACK";

/** Short per-type breakdown for a client row, e.g. "120 trackers · 30 GPS locks". */
export function deviceMix(devicesByType) {
  const one = {GT06_TRACKER: "tracker", GPS_LOCK: "GPS lock", BT_LOCK: "BLE lock", MOBI_TRACK: "Track Point"};
  const many = {GT06_TRACKER: "trackers", GPS_LOCK: "GPS locks", BT_LOCK: "BLE locks", MOBI_TRACK: "Track Point"};
  return DEVICE_TYPES.filter((t) => (devicesByType?.[t] || 0) > 0)
    .map((t) => `${devicesByType[t].toLocaleString("en-IN")} ${devicesByType[t] === 1 ? one[t] : many[t]}`)
    .join(" · ");
}

/** Online share for the client row's bar, 0–100. */
export const onlinePercent = (row) =>
  row?.liveStatusDevices > 0 ? Math.round((row.online * 100) / row.liveStatusDevices) : 0;
